import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventClassRegistrationResponse, HorseResponse, UserResponse} from '../../../../../../../../../../../../@shared/models/response.module';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {EventsService} from '../../../../../../../../../../../../@shared/services/events-service';
import {Horses} from '../../../../../../../../../../../../@shared/services/horses';
import {ToastService} from '../../../../../../../../../../../../@core/services/toast.service';
import {_AQH_EVE_34, AQH_HOR_0} from '../../../../../../../../../../../../@shared/models/input.module';
import {EndpointService} from '../../../../../../../../../../../../@shared/endpoints.service';
import {AddHorse} from '../../../../../../../../../../../../pages/user-profile/components/user-profile-horses/components/add-horse/add-horse';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';

interface UserSearchResult {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  full_name: string;
}

@Component({
  selector: 'app-edit-registration',
  standalone: false,
  templateUrl: './edit-registration.html',
  styleUrl: './edit-registration.css'
})
export class EditRegistration implements OnInit {
  @Input() registration!: EventClassRegistrationResponse;

  form: FormGroup;
  saving = false;
  loadingHorses = false;
  isSearchingUsers = false;

  availableHorses: HorseResponse[] = [];
  userSearchResults: UserSearchResult[] = [];

  selectedUser: UserResponse | null = null;
  selectedHorse: HorseResponse | null = null;

  userSearchTerm = '';
  private searchSubject = new Subject<string>();

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private eventsService: EventsService,
    private horsesService: Horses,
    private toastService: ToastService,
    private endpointService: EndpointService,
    private modalService: NgbModal
  ) {
    this.form = this.fb.group({
      user_id: ['', Validators.required],
      horse_id: [null, Validators.required],
      notes: ['']
    });
  }

  ngOnInit() {
    if (this.registration) {
      this.selectedUser = this.registration.user || null;
      this.selectedHorse = this.registration.horse || null;

      if (this.selectedUser) {
        this.userSearchTerm = `${this.selectedUser.first_name} ${this.selectedUser.last_name}`;
      }

      this.form.patchValue({
        user_id: this.registration.user_id,
        notes: this.registration.notes || ''
      });
    }

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < 2) {
          return [];
        }
        this.isSearchingUsers = true;
        return this.endpointService.aqh_eve_67({ search: query });
      })
    ).subscribe({
      next: (response: any) => {
        this.isSearchingUsers = false;
        this.userSearchResults = response?.data || [];
      },
      error: () => {
        this.isSearchingUsers = false;
        this.userSearchResults = [];
      }
    });

    this.loadAllHorses();
  }

  loadAllHorses() {
    this.loadingHorses = true;
    const payload: AQH_HOR_0 = {};

    this.horsesService.getHorses(payload).subscribe({
      next: (horses: HorseResponse[]) => {
        this.availableHorses = horses || [];
        this.loadingHorses = false;

        if (this.registration && this.registration.horse_id) {
          setTimeout(() => {
            this.form.patchValue({
              horse_id: this.registration.horse_id
            }, { emitEvent: true });

            this.selectedHorse = this.availableHorses.find(h => h.horse_id === this.registration.horse_id) || this.selectedHorse;
            this.form.get('horse_id')?.updateValueAndValidity();
          }, 100);
        }
      },
      error: () => {
        this.loadingHorses = false;
        this.availableHorses = [];
        this.toastService.error('Failed to load horses');
      }
    });
  }

  onUserSearch() {
    this.searchSubject.next(this.userSearchTerm);
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user as any;
    this.form.patchValue({ user_id: user.user_id });
    this.userSearchTerm = user.full_name || `${user.first_name} ${user.last_name}`;
    this.userSearchResults = [];
  }

  clearUserSearch() {
    this.userSearchTerm = '';
    this.selectedUser = null;
    this.form.patchValue({ user_id: '' });
    this.userSearchResults = [];
  }

  onHorseChanged(horseId: any) {
    this.form.patchValue({ horse_id: horseId });
    this.selectedHorse = this.availableHorses.find(h => h.horse_id === horseId) || null;
  }

  openAddHorseModal() {
    if (!this.selectedUser) {
      this.toastService.warning('Please select a user first');
      return;
    }

    const modalRef = this.modalService.open(AddHorse, { size: 'lg', centered: true });

    modalRef.result.then((newHorse: HorseResponse) => {
      if (newHorse) {
        this.toastService.success('Horse added successfully');
        this.loadAllHorses();
      }
    }, () => {});
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const payload: _AQH_EVE_34 = {
      class_registration_id: this.registration.class_registration_id,
      user_id: this.form.get('user_id')!.value,
      horse_id: this.form.get('horse_id')!.value,
      notes: this.form.get('notes')!.value || ''
    };

    this.eventsService.updateEventClassRegistration(payload).subscribe({
      next: () => {
        this.saving = false;
        this.toastService.success('Registration updated successfully');
        this.activeModal.close(true);
      },
      error: () => {
        this.saving = false;
        this.toastService.error('Failed to update registration');
      }
    });
  }

  close() {
    this.activeModal.close();
  }

  get userIdInvalid(): boolean {
    const c = this.form.get('user_id');
    return !!(c && c.invalid && c.touched);
  }

  get horseIdInvalid(): boolean {
    const c = this.form.get('horse_id');
    return !!(c && c.invalid && c.touched);
  }
}
